/**
 * StudioMessage.js
 *
 * Renders a single chat message bubble.
 * Supports: user / agent text, streaming cursor, file chips, analysis result chips.
 */
import React from 'react';

const MSG_REGEX = /"message"\s*:\s*"((?:\\.|[^"\\])*)"/s;
const PHASE_LABELS = {
  requirements: 'Requirements',
  design: 'Design',
  documentation: 'Documentation',
  completed: 'Completed',
};

function toBase64Url(input = '') {
  const bytes = new TextEncoder().encode(String(input || ''));
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function extractMermaidRaw(content = '') {
  if (!content) return '';
  return String(content)
    .replace(/```mermaid/gi, '')
    .replace(/```/g, '')
    .trim();
}

function getDiagramImageCandidates(diagram = {}) {
  const candidates = [];

  if (Array.isArray(diagram.image_urls)) {
    candidates.push(...diagram.image_urls);
  }

  if (diagram.image_url) {
    candidates.push(diagram.image_url);
  }

  const raw = extractMermaidRaw(diagram.content);
  if (raw) {
    const encoded = toBase64Url(raw);
    candidates.push(`https://mermaid.ink/img/${encoded}`);
    candidates.push(`https://quickchart.io/mermaid?graph=${encodeURIComponent(raw)}`);
  }

  const priority = (url) => {
    const u = String(url || '').toLowerCase();
    if (u.includes('mermaid.ink')) return 0;
    if (u.includes('quickchart.io')) return 2;
    return 1;
  };

  const seen = new Set();
  return candidates
    .map((u) => String(u || '').trim())
    .filter((u) => /^https?:\/\//i.test(u))
    .sort((a, b) => priority(a) - priority(b))
    .filter((u) => {
      if (seen.has(u)) return false;
      seen.add(u);
      return true;
    });
}

function getDiagramDisplayName(diagram = {}) {
  if (diagram?.display_name) return String(diagram.display_name);
  const raw = String(diagram?.name || 'design_diagram').replace(/\.mmd$/i, '').replace(/[_\-]+/g, ' ').trim();
  return raw || 'Design Diagram';
}

function normalizeMessageText(text) {
  if (typeof text !== 'string') return '';
  const trimmed = text.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed.message === 'string') return parsed.message;
    } catch {
      const match = trimmed.match(MSG_REGEX);
      if (match?.[1]) {
        try {
          return JSON.parse(`"${match[1]}"`);
        } catch {
          return match[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
        }
      }
    }
  }

  return text;
}

/** Very lightweight inline markdown: **bold**, `code`, newlines, bullet lines. */
function renderMd(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, li) => {
    // Bullet
    const isBullet = /^[-*•]\s/.test(line);
    const raw = isBullet ? line.replace(/^[-*•]\s/, '') : line;

    // Tokenise: **bold** and `code`
    const tokens = raw.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    const nodes = tokens.map((t, ti) => {
      if (t.startsWith('**') && t.endsWith('**'))
        return <strong key={ti}>{t.slice(2, -2)}</strong>;
      if (t.startsWith('`') && t.endsWith('`'))
        return <code key={ti}>{t.slice(1, -1)}</code>;
      return t;
    });

    if (isBullet) return <div key={li} style={{ paddingLeft: 14, position: 'relative' }}>
      <span style={{ position: 'absolute', left: 2 }}>•</span>{nodes}
    </div>;

    return <span key={li}>{nodes}{li < lines.length - 1 && <br />}</span>;
  });
}

function getResultPhase(result = {}) {
  if (result?.current_phase) return result.current_phase;
  if (result?.design_document) return 'design';
  if (result?.documentation_bundle) return 'documentation';
  return 'requirements';
}

function getDesignDiagrams(result = {}) {
  const artifacts = result?.design_document?.diagram_artifacts;
  if (!Array.isArray(artifacts)) return [];
  return artifacts.filter((diagram) => diagram && (diagram.image_url || diagram.content));
}

function DiagramPreview({ diagram, variant = 'inline' }) {
  const candidates = React.useMemo(() => getDiagramImageCandidates(diagram), [diagram]);
  const [imageIdx, setImageIdx] = React.useState(0);
  const [renderFailed, setRenderFailed] = React.useState(false);
  const currentSrc = candidates[imageIdx] || '';

  React.useEffect(() => {
    setImageIdx(0);
    setRenderFailed(false);
  }, [diagram]);

  return (
    <>
      {currentSrc && (
        <a href={currentSrc} target="_blank" rel="noreferrer" className={`studio-${variant}-diagram-link`}>
          <img
            src={currentSrc}
            alt={diagram.name || 'Design diagram'}
            className={`studio-${variant}-diagram-img`}
            loading="lazy"
            onLoad={() => setRenderFailed(false)}
            onError={() => {
              if (imageIdx < candidates.length - 1) {
                setImageIdx(imageIdx + 1);
              } else {
                setRenderFailed(true);
              }
            }}
          />
        </a>
      )}
      {(!currentSrc || renderFailed) && diagram.content && (
        <pre className={`studio-${variant}-diagram-pre`}>{diagram.content}</pre>
      )}
    </>
  );
}

function StudioMessage({
  message,
  isStreaming,
  streamingContent,
  onShowResult,
  onRetryUserMessage,
  onCopyMessage,
  isBusy = false,
}) {
  const isUser   = message.role === 'user';
  const isStream = isStreaming && !isUser;

  const content = isStream
    ? normalizeMessageText(streamingContent)
    : normalizeMessageText(message.content);
  const phase = message.analysisResult ? getResultPhase(message.analysisResult) : null;
  const diagrams = message.analysisResult ? getDesignDiagrams(message.analysisResult) : [];

  return (
    <div className={`studio-msg ${isUser ? 'user' : 'agent'}`}>
      {/* Avatar */}
      <div className="studio-msg-avatar">
        {isUser ? '👤' : '🤖'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* File chip (if message has file attachment) */}
        {message.fileRef && (
          <div className="studio-file-chip">
            📎 {message.fileRef.name}
          </div>
        )}

        {/* Main bubble */}
        <div className="studio-msg-bubble">
          {renderMd(content)}
          {isStream && <span className="studio-cursor" />}
        </div>

        {!isStream && (
          <div className={`studio-msg-actions ${isUser ? 'user' : 'agent'}`}>
            {isUser && (
              <button
                type="button"
                className="studio-msg-action-btn"
                onClick={onRetryUserMessage}
                disabled={isBusy}
                title="Refresh this message"
                aria-label="Refresh this message"
              >
                ↻
              </button>
            )}
            <button
              type="button"
              className="studio-msg-action-btn"
              onClick={onCopyMessage}
              title="Copy message"
              aria-label="Copy message"
            >
              📋
            </button>
          </div>
        )}

        {/* Analysis result chip (attached to agent messages that carry analysis) */}
        {message.analysisResult && (
          <AnalysisChip
            result={message.analysisResult}
            phase={phase}
            onShow={() => onShowResult && onShowResult(message.analysisResult)}
          />
        )}

        {diagrams.length > 0 && (
          <div className="studio-inline-diagrams">
            {diagrams.map((diagram, idx) => (
              <div className="studio-inline-diagram" key={`${diagram.name || 'diagram'}_${idx}_${diagram.image_url || ''}`}>
                <div className="studio-inline-diagram-title">{getDiagramDisplayName(diagram)}</div>
                {diagram?.explanation && (
                  <div className="studio-inline-diagram-explanation">{String(diagram.explanation)}</div>
                )}
                <DiagramPreview diagram={diagram} variant="inline" />
              </div>
            ))}
          </div>
        )}

        {/* Timestamp */}
        {!isStream && (
          <div style={{ fontSize: '0.68rem', color: '#cbd5e1',
                        textAlign: isUser ? 'right' : 'left', marginTop: 2 }}>
            {formatTs(message.ts)}
          </div>
        )}
      </div>
    </div>
  );
}

function AnalysisChip({ result, phase, onShow }) {
  const pct = Math.round((result.score / 10) * 100);
  const phaseLabel = PHASE_LABELS[phase || 'requirements'] || 'Requirements';
  const statusLabel = result.valid ? `✅ Valid ${phaseLabel}` : `❌ Incomplete ${phaseLabel}`;
  return (
    <div className="studio-analysis-chip">
      <div className="studio-analysis-chip-row">
        <div>
          <span className={`studio-valid-badge ${result.valid ? 'valid' : 'invalid'}`}>
            {statusLabel} · {result.score}/10
          </span>
        </div>
        <button className="studio-show-result-btn" onClick={onShow}>
          View Full Analysis
        </button>
      </div>
      <div className="studio-score-bar" style={{ marginTop: 8 }}>
        <div className="studio-score-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1)  return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24)   return `${diffH}h ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

export default StudioMessage;
